/**
 * Paginación de lecturas de Supabase.
 *
 * PostgREST corta CADA request en ~1000 filas (el `max-rows` del server). Una
 * query sin paginar no falla ni avisa: devuelve 1000 filas y se calla. El
 * síntoma aparece mucho después, como un total de horas subvaluado en una
 * pantalla y correcto en otra, o un reporte que "le faltan movimientos".
 *
 * Dos cosas hacen falta para traer todo bien, y la segunda es la que se olvida:
 *
 *   1. Pedir páginas hasta que una venga incompleta.
 *   2. Que el orden sea DETERMINISTA. Si dos filas empatan en todas las columnas
 *      del `order by`, Postgres puede devolverlas en distinto orden en cada
 *      request: la misma fila sale en la página 1 y también en la 2 (duplicada)
 *      mientras otra no sale en ninguna (perdida). Por eso este helper agrega
 *      siempre un desempate por columna única, y no confía en que el llamador
 *      se haya acordado.
 *
 * Vive acá y no dentro de un módulo del CRM porque `crmCommercial.js` importa
 * `crmWorkflow.js`: cualquiera de los dos que exportara esto crearía un ciclo.
 *
 * Este archivo no importa el cliente de supabase a propósito — recibe una
 * fábrica de queries. Así es lógica pura y se testea con `node --test`.
 * Tests en `tests/supabase-paginacion.test.mjs`.
 */

export const SUPABASE_PAGE_SIZE = 1000;

/* Tope de seguridad: freno para una query mal filtrada, no un límite de negocio.
   Alcanzarlo devuelve error (ver abajo), así que conviene que sea alto: las
   lecturas que de verdad tenían volumen —las ventas de una organización, 14.029
   en producción— ya se acotan por id con `fetchRowsByIds`. */
export const SUPABASE_MAX_ROWS = 50000;

/**
 * Trae todas las filas de una query, paginando.
 *
 * @param {() => object} queryBuilderFactory
 *   Fábrica que devuelve una query NUEVA en cada llamada. Tiene que ser una
 *   fábrica y no una query ya construida: supabase-js muta el builder, así que
 *   reusarlo acumularía `range` sobre `range` y devolvería basura.
 * @param {object} [options]
 * @param {string|null} [options.tieBreaker='id']
 *   Columna única por la que desempatar. `null` solo si la query YA ordena por
 *   una columna única — y en ese caso conviene dejarlo escrito por qué.
 * @param {number} [options.pageSize=SUPABASE_PAGE_SIZE]
 * @param {number} [options.maxRows=SUPABASE_MAX_ROWS]
 * @param {string} [options.label] Para el warning de truncado.
 *
 * @returns {Promise<{data: Array|null, error: object|null, truncated: boolean}>}
 *   Ante error devuelve `{ data: null, error }` con la forma que esperan
 *   `unwrapResponse` / `unwrapOptionalRows`. Nunca devuelve filas parciales
 *   junto a un error: media tabla presentada como completa es peor que fallar,
 *   porque nadie se entera.
 */
export async function fetchAllRows(queryBuilderFactory, options = {}) {
  const {
    tieBreaker = 'id',
    pageSize = SUPABASE_PAGE_SIZE,
    maxRows = SUPABASE_MAX_ROWS,
    label = 'fetchAllRows',
  } = options;

  const rows = [];
  let from = 0;

  while (from < maxRows) {
    let query = queryBuilderFactory();
    if (tieBreaker) query = query.order(tieBreaker, { ascending: true });

    const response = await query.range(from, from + pageSize - 1);
    if (response?.error) {
      return { data: null, error: response.error, truncated: false };
    }

    const page = response?.data || [];
    rows.push(...page);
    /* Se corta cuando una página viene VACÍA, no cuando viene "más chica que
       pageSize", y se avanza por las filas realmente recibidas. Parece un
       request de más y lo es —uno por lectura—, pero es lo que hace al helper
       inmune al `max-rows` del servidor: ese parámetro se edita desde el
       dashboard de Supabase, y si alguien lo baja a 500 la versión anterior
       cortaba en la primera página y reportaba la lectura como completa. O sea,
       exactamente el bug que este archivo existe para eliminar, con un helper
       encima asegurando que estaba resuelto. */
    if (page.length === 0) break;
    from += page.length;
  }

  /* Alcanzar el tope devuelve ERROR, no las filas que llegaron. Devolverlas con
     un flag `truncated` que la UI no mira es el bug original con otro nombre:
     la pantalla muestra un total de menos y nadie se entera nunca. Un error
     sube por `unwrapResponse` y termina en un toast que el usuario ve.
     Si esto salta en producción, la respuesta no es subir el tope: es acotar la
     query (por fecha, o por ids con `fetchRowsByIds`). */
  if (rows.length >= maxRows) {
    console.warn(`[${label}] se alcanzó el tope de ${maxRows} filas`);
    return {
      data: null,
      truncated: true,
      error: {
        code: 'PAGINACION_TOPE',
        message: `La consulta "${label}" superó las ${maxRows} filas: la lectura quedaría incompleta. Acotá el rango de fechas o el filtro.`,
      },
    };
  }

  return { data: rows, error: null, truncated: false };
}

/** Máximo de ids por request. La URL de PostgREST revienta cerca de los 400 UUIDs. */
export const SUPABASE_ID_CHUNK_SIZE = 200;

/** Chunks simultáneos. Más que esto encola contra el statement timeout del pooler. */
export const SUPABASE_CHUNK_CONCURRENCY = 4;

/**
 * Trae filas acotadas a una lista de ids, en chunks y paginando cada uno.
 *
 * Es la alternativa a leer una tabla entera para después buscarle un puñado de
 * filas por id. Concreto: una organización de producción tiene 14.029 ventas
 * activas; el panel de administración del CRM las traía todas para resolver
 * ~12 `liquidated_sale_id`. Eso son 15 round-trips, y a 20.000 filas
 * `fetchAllRows` trunca y el importe liquidado sale de menos sin fallar.
 *
 * Chunkea porque `id=in.(…)` viaja en la URL: ~39 bytes por UUID, así que a
 * partir de unos 400 ids el request se cae.
 *
 * @param {(chunk: string[]) => object} queryBuilderFactory
 *   Recibe el chunk de ids y devuelve la query armada, p. ej.
 *   `(chunk) => supabase.from('sales').select('id, total').in('id', chunk)`.
 * @param {Array<string|null|undefined>} ids
 *   Se limpian los falsy y se deduplican: estos ids salen de columnas nullable
 *   (`liquidated_sale_id`, `triggered_sale_id`) y sin limpiar se pediría
 *   `id=in.(null,null,…)`.
 * @param {object} [options] Acepta lo mismo que `fetchAllRows`, más `chunkSize`.
 * @returns {Promise<{data: Array|null, error: object|null, truncated: boolean}>}
 */
export async function fetchRowsByIds(queryBuilderFactory, ids = [], options = {}) {
  const {
    chunkSize = SUPABASE_ID_CHUNK_SIZE,
    concurrency = SUPABASE_CHUNK_CONCURRENCY,
    ...pageOptions
  } = options;

  const unicos = [...new Set((ids || []).filter(Boolean))];
  if (!unicos.length) return { data: [], error: null, truncated: false };

  const chunks = [];
  for (let i = 0; i < unicos.length; i += chunkSize) {
    chunks.push(unicos.slice(i, i + chunkSize));
  }

  /* Los chunks van en paralelo pero DE A POCOS. En serie, el costo crece con la
     cantidad de ids y a partir de ~1600 esto haría más viajes de ida y vuelta
     que leer la tabla entera —justo el problema que vino a resolver—, encima
     uno detrás del otro. Sin límite, en cambio, 10.000 ids salen como 50
     lecturas simultáneas y encolan contra el statement timeout del pooler. */
  const responses = [];
  for (let i = 0; i < chunks.length; i += concurrency) {
    const tanda = chunks.slice(i, i + concurrency);
    const resultados = await Promise.all(
      tanda.map((chunk) => fetchAllRows(() => queryBuilderFactory(chunk), pageOptions)),
    );
    const conError = resultados.find((response) => response.error);
    if (conError) return { data: null, error: conError.error, truncated: false };
    responses.push(...resultados);
  }

  return {
    data: responses.flatMap((response) => response.data),
    error: null,
    truncated: responses.some((response) => response.truncated),
  };
}
